import { useBoolean } from "@/hooks/useBoolean";
import { useTranslations } from "@/i18n/client";
import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";

import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import useModal from "@/hooks/useModal";
import useLocation from "@/lib/centra/hooks/useLocation";
import clsx from "clsx";
import isEqual from "lodash.isequal";
import { BackInStockCheckbox } from "./BackInStockCheckbox";
import styles from "./coming-soon-modal.module.css";

const NotifyBackInStock = ({
  item,
}: {
  item?: { itemId: string; stock: boolean };
}) => {
  const t = useTranslations();
  const [email, setEmail] = useState("");
  const checkboxState = useBoolean(false);
  const location = useLocation();
  const [locationState, setLocationState] = useState(location.state);
  const [country, setCountry] = useState(location.location);
  const [result, setResult] = useState(false);
  const { closeModal, isOpen } = useModal(BACK_IN_STOCK_MODAL_KEY, {
    _lock: true,
    popover: false,
  });

  useEffect(() => {
    if (!isOpen) {
      setResult(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isEqual(location.location, country)) {
      return;
    }
    setCountry(location.location);
    setLocationState(location.state);
  }, [location, country]);

  const notifyMeComingSoon = async () => {
    const apiUrl = process.env.NEXT_PUBLIC_CENTRA_CHECKOUT;
    const res = await fetch(`${apiUrl}/newsletter-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        country: country,
        item: item?.itemId,
      }),
    });
    const json = await res.json();
    setResult(json.subscribed);
  };

  const notifyMeStock = async (e: FormEvent) => {
    e.preventDefault();
    if (checkboxState.value) {
      notifyMeComingSoon();
    }
    const apiUrl = process.env.NEXT_PUBLIC_CENTRA_CHECKOUT;
    const res = await fetch(`${apiUrl}/back-in-stock-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        shipTo: {
          country: country,
          state: locationState,
        },
        item: item?.itemId,
      }),
    });
    const json = await res.json();
    setResult(!!json.token);
  };

  const toggleCheckboxState = (_e: ChangeEvent<HTMLInputElement>) => {
    checkboxState.toggle();
  };

  return (
    <Modal title={" "} modal={BACK_IN_STOCK_MODAL_KEY} always>
      <div className={styles.inner}>
        {!result ? (
          <>
            <h2>Notify when back in stock</h2>

            <p className={styles.ingress}>{t("checkout.backInStock")}</p>
            <form onSubmit={notifyMeStock}>
              <div className={styles["inputs"]}>
                <input type="hidden" name="_country" value={country ?? 0} />
                <input type="hidden" name="_item" value={item?.itemId ?? 0} />
                <input type="hidden" name="_state" value={locationState ?? 0} />
                <input
                  type="email"
                  placeholder="Your e-mail"
                  id="email"
                  name="email"
                  value={email}
                  required
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <div className={styles["modal-button-container"]}>
                <Button className="uppercase">Notify me</Button>
              </div>
              <div className={styles.checkbox}>
                <BackInStockCheckbox
                  checked={checkboxState.value}
                  onChange={toggleCheckboxState}
                />
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className={clsx(styles["text-center"])}>
              {t("backInStock.title")}
            </h2>
            <p className={clsx(styles["text-center"])}>
              {t("backInStock.text")}
            </p>
            <Button onClick={closeModal}>{t("back")}</Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NotifyBackInStock;

export const BACK_IN_STOCK_MODAL_KEY = "notify";
